import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MessageCircle, MessagesSquare, Loader2, Copy, ChevronLeft, ChevronRight, Eye, CalendarDays, Hash, Play } from "lucide-react";
import { VideoPoster } from "@/components/listing/Gallery";
import { api, imageUrl } from "@/lib/api";
import { categoryName, subcategoryName } from "@/lib/constants";

export const MediaTitle = ({ children, testid }) => (
  <h2 className="font-headline font-extrabold text-lg sm:text-xl text-slate-900 flex items-center gap-2 mb-2.5" data-testid={testid}>
    <span className="w-1.5 h-5 rounded-full bg-[#1A5C3A]" />{children}
  </h2>
);

const scrollToGallery = () => {
  const g = [...document.querySelectorAll('[data-testid="detail-gallery"]')].find((el) => el.offsetParent !== null);
  g?.scrollIntoView({ behavior: "smooth", block: "center" });
};

const Thumb = ({ m, i, idx, setIdx }) => (
  <button onClick={() => { setIdx(i); if (m.type === "video") scrollToGallery(); }} className={`relative shrink-0 w-24 h-[72px] sm:w-28 sm:h-20 rounded-xl overflow-hidden border-2 cursor-pointer transition-opacity ${i === idx ? "border-[#1A5C3A]" : "border-transparent opacity-70 hover:opacity-100"}`} data-testid={m.type === "video" ? "detail-thumb-video" : `detail-thumb-${i}`}>
    {m.type === "video" ? <>
      <VideoPoster m={m} className="w-full h-full object-cover bg-black" />
      <span className="absolute inset-0 flex items-center justify-center bg-black/30"><Play className="w-6 h-6 text-white fill-current" /></span>
    </> : <img src={imageUrl(m.src)} alt="" className="w-full h-full object-cover" />}
  </button>
);

// Thumbnails under the gallery: image thumbs, then «فيديو الإعلان» with the video thumb (only if the ad has a video).
export function Thumbs({ media, idx, setIdx, lang }) {
  const L = (ar, en) => (lang === "en" ? en : ar);
  const imgs = media.filter((m) => m.type === "image");
  const vi = media.findIndex((m) => m.type === "video");
  if (media.length < 2 && vi < 0) return null;
  return (
    <div className="mt-3 space-y-4">
      {imgs.length > 1 && <div className="flex gap-2.5 overflow-x-auto no-scrollbar" data-testid="detail-thumbs">{imgs.map((m, i) => <Thumb key={i} m={m} i={i} idx={idx} setIdx={setIdx} />)}</div>}
      {vi >= 0 && <div data-testid="detail-video-block"><MediaTitle testid="detail-video-title">{L("فيديو الإعلان", "Ad video")}</MediaTitle><Thumb m={media[vi]} i={vi} idx={idx} setIdx={setIdx} /></div>}
    </div>
  );
}

export function Breadcrumb({ listing, lang }) {
  const Sep = lang === "en" ? ChevronRight : ChevronLeft;
  const sub = subcategoryName(listing.category, listing.subcategory, lang);
  const items = [["/", lang === "en" ? "Home" : "الرئيسية"], [`/category/${listing.category}`, categoryName(listing.category, lang)], ...(sub ? [[`/category/${listing.category}/${listing.subcategory}`, sub]] : [])];
  return (
    <nav className="flex items-center flex-wrap gap-1.5 text-sm text-slate-500" data-testid="detail-breadcrumb">
      {items.map(([to, label], i) => (
        <span key={to} className="flex items-center gap-1.5">
          {i > 0 && <Sep className="w-3.5 h-3.5" />}
          <Link to={to} className="hover:text-[#1A5C3A] hover:underline" data-testid={`detail-breadcrumb-${i}`}>{label}</Link>
        </span>
      ))}
    </nav>
  );
}

export function Stats({ listing, lang }) {
  const items = [
    [Eye, String(listing.views ?? 0), lang === "en" ? "views" : "مشاهدة"],
    [CalendarDays, listing.created_at ? new Date(listing.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "ar-AE-u-nu-latn") : "—", ""],
    [Hash, listing.id, ""],
  ];
  return (
    <div className="flex items-center flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500" data-testid="detail-stats">
      {items.map(([I, v, s], i) => <span key={i} className="flex items-center gap-1.5"><I className="w-4 h-4 text-[#1A5C3A]" /><span dir="auto">{v}</span>{s}</span>)}
    </div>
  );
}

export function ContactPanel({ phone, whatsapp, onChat, starting, listingId, lang }) {
  const T = (ar, en) => (lang === "en" ? en : ar);
  const [shown, setShown] = useState(false);
  const wa = (whatsapp || "").replace(/[^0-9]/g, "");
  const track = (kind) => listingId && api.post(`/listings/${listingId}/contact`, { kind }).catch(() => {});
  const base = "w-full h-12 rounded-2xl font-bold flex items-center justify-center gap-2 transition-opacity";
  const copy = async () => { try { await navigator.clipboard.writeText(phone); toast.success(T("تم نسخ الرقم", "Number copied")); } catch { toast.error(T("تعذّر النسخ", "Copy failed")); } };
  return (
    <div className="space-y-2.5" data-testid="detail-contact-panel">
      {shown && phone ? (
        <div className="h-12 rounded-2xl border-2 border-[#1A5C3A] flex items-center justify-between px-4" data-testid="detail-phone-revealed">
          <a href={`tel:${phone}`} dir="ltr" className="font-extrabold text-lg text-[#1A5C3A]" data-testid="detail-phone-number">{phone}</a>
          <button onClick={copy} className="flex items-center gap-1 text-sm font-semibold text-[#1A5C3A]" data-testid="detail-phone-copy"><Copy className="w-4 h-4" />{T("نسخ", "Copy")}</button>
        </div>
      ) : (
        <button disabled={!phone} onClick={() => { setShown(true); track("call"); }} className={`${base} bazaro-gradient text-white disabled:opacity-40`} data-testid="detail-show-phone">
          <Phone className="w-5 h-5" />{T("عرض رقم الهاتف", "Show phone number")}
        </button>
      )}
      <a href={wa ? `https://wa.me/${wa}` : undefined} target="_blank" rel="noreferrer" onClick={() => track("whatsapp")} aria-disabled={!wa} className={`${base} bg-[#25D366] text-white ${wa ? "" : "opacity-40 pointer-events-none"}`} data-testid="detail-whatsapp">
        <MessageCircle className="w-5 h-5" />{T("واتساب", "WhatsApp")}
      </a>
      <button onClick={() => { track("chat"); onChat(); }} disabled={starting} className={`${base} bg-[#E8EFEA] text-[#0F4D32]`} data-testid="detail-chat">
        {starting ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessagesSquare className="w-5 h-5" />}{T("مراسلة البائع", "Message seller")}
      </button>
    </div>
  );
}
